import React, { useState } from "react";
import { Move, Charge } from "@/types";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  useChargeActionsMutation,
  useRefundChargeMutation,
} from "@/store/charges/apis";
import { format, parseISO } from "date-fns";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Loader2, RotateCcwIcon } from "lucide-react";
import { CHARGE_PURPOSES, PAYMENT_METHODS } from "@/constants";
import { showCardDetails } from "@/lib/utils";
import { RecordOfflinePaymentSheet } from "@/pages/Moves/components/RecordOfflinePaymentSheet";

interface PaymentsProps {
  move: Move;
  refetchMove: () => void;
  isOfflinePaymentSheetOpen: boolean;
  setIsOfflinePaymentSheetOpen: (open: boolean) => void;
}

const Payments: React.FC<PaymentsProps> = ({
  move,
  refetchMove,
  isOfflinePaymentSheetOpen,
  setIsOfflinePaymentSheetOpen,
}) => {
  const [chargeActions] = useChargeActionsMutation();
  const [isMarkPaidDialogOpen, setIsMarkPaidDialogOpen] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [selectedChargeId, setSelectedChargeId] = useState<number | null>(null);
  const [charge, setCharge] = useState<Charge | null>(null);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [refundCharge, { isLoading: isRefunding }] = useRefundChargeMutation();
  const { toast } = useToast();

  if (!move.charges || move.charges.length === 0) {
    return (
      <div className="w-full">
        <div className="text-sm text-gray-500">
          No payments recorded for this move yet.
        </div>
        <RecordOfflinePaymentSheet
          open={isOfflinePaymentSheetOpen}
          onOpenChange={setIsOfflinePaymentSheetOpen}
          moveId={move.id.toString()}
          onSuccess={refetchMove}
        />
      </div>
    );
  }

  // Sort charges in reverse chronological order
  const sortedCharges = [...(move.charges || [])].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const totalAmount = (move.charges || []).reduce(
    (acc, charge) => acc + Number(charge.amount) || 0,
    0
  );

  const handleMarkAsPaid = async () => {
    if (!selectedChargeId) return;

    try {
      await chargeActions({
        charge_id: selectedChargeId.toString(),
        action: "mark_as_paid",
      }).unwrap();
      toast({
        title: "Payment marked as paid successfully",
        description: "The payment has been marked as paid.",
      });
      refetchMove();
    } catch (error) {
      toast({
        title: "Failed to mark payment as paid",
        description: "Please try again.",
      });
      console.error("Error marking payment as paid:", error);
    } finally {
      setIsMarkPaidDialogOpen(false);
      setSelectedChargeId(null);
    }
  };

  const handleVoidCharge = async () => {
    if (!selectedChargeId) return;

    try {
      await chargeActions({
        charge_id: selectedChargeId.toString(),
        action: "void",
      }).unwrap();
      toast({
        title: "Payment voided successfully",
        description: "The payment has been voided.",
      });
      refetchMove();
    } catch (error) {
      toast({
        title: "Failed to cancel payment",
        description: "Please try again.",
      });
      console.error("Error voiding payment:", error);
    } finally {
      setIsVoidDialogOpen(false);
      setSelectedChargeId(null);
    }
  };

  const handleRefundCharge = async () => {
    if (!charge || !refundAmount || refundAmount === 0) return;

    try {
      await refundCharge({
        id: charge.id,
        amount: refundAmount,
      }).unwrap();

      toast({
        title: "Payment refunded successfully",
        description: "The payment has been refunded to the customer.",
      });

      refetchMove();
      setRefundAmount(null);
      setSelectedChargeId(null);
      setIsRefundDialogOpen(false);
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Failed to refund payment",
          description: error.message,
        });
      } else {
        toast({
          title: "Failed to refund payment",
          description: (error as any).data.error,
        });
      }
      console.error("Error refunding payment:", error);
    }
  };
  const getPurposeLabel = (purpose: string) => {
    return CHARGE_PURPOSES[purpose as keyof typeof CHARGE_PURPOSES] || purpose;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, { label: string; className?: string }> = {
      pending: {
        label: "Pending",
        // className: "text-amber-600 font-medium",
      },
      paid: {
        label: "Paid",
        // className: "text-green-600 font-medium",
      },
      voided: {
        label: "Canceled",
        // className: "text-red-600 font-medium",
      },
      failed: {
        label: "Failed",
        // className: "text-red-600 font-medium",
      },
      processed: {
        label: "Processed",
        // className: "text-green-600 font-medium",
      },
      refunded: {
        label: "Refunded",
        // className: "text-red-600 font-medium",
      },
      partially_refunded: {
        label: "Partially Refunded",
        // className: "text-red-600 font-medium",
      },
      fully_refunded: {
        label: "Fully Refunded",
        // className: "text-red-600 font-medium",
      },
    };
    return statusMap[status] || { label: status, className: "text-gray-600" };
  };

  const pendingActions = (charge: Charge) => {
    return (
      <>
        <Button
          size="sm"
          variant="outline"
          className="text-green-700 border-green-600 hover:bg-green-50"
          onClick={() => {
            setSelectedChargeId(charge.id);
            setIsMarkPaidDialogOpen(true);
          }}
        >
          Mark as Paid
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-red-600 border-red-600 hover:bg-red-50"
          onClick={() => {
            setSelectedChargeId(charge.id);
            setIsVoidDialogOpen(true);
          }}
        >
          Cancel
        </Button>
      </>
    );
  };

  const refundAction = (c: Charge) => {
    const isOpen = isRefundDialogOpen && charge?.id === c.id;

    return (
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            setCharge(c);
            setIsRefundDialogOpen(true);
          } else {
            setRefundAmount(null);
            setCharge(null);
            setIsRefundDialogOpen(false);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="text-green-600 border-green-600 hover:bg-green-50 flex items-center gap-1"
            onClick={() => {
              setCharge(c);
              setIsRefundDialogOpen(true);
            }}
          >
            <RotateCcwIcon className="w-3 h-3" />
            Issue Refund
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="space-y-0.5">
                <h4 className="font-medium">Refund</h4>
                <div className="text-sm text-gray-700">
                  Refund will be issued to the original card. Customer will
                  receive an email with the refund details.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                    $
                  </span>
                  <Input
                    className="pl-6 w-full md:w-24"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setRefundAmount(Math.min(val, remainingAmount(c)));
                    }}
                  />
                </div>
                <div className="text-sm text-gray-700">
                  (max:{" "}
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(remainingAmount(c))}
                  )
                </div>
              </div>
            </div>
            <div className="flex justify-start gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRefundAmount(null);
                  setCharge(null);
                  setIsRefundDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-green-700 hover:bg-green-700 text-white flex items-center gap-0.5"
                onClick={() => {
                  handleRefundCharge();
                }}
                disabled={!refundAmount || refundAmount === 0 || isRefunding}
              >
                {isRefunding ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Refunding...
                  </>
                ) : (
                  "Issue Refund"
                )}
                {refundAmount && (
                  <span className="pl-0.5">
                    (
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(refundAmount || 0)}
                    )
                  </span>
                )}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };
  const remainingAmount = (charge: Charge | null) => {
    if (!charge) return 0;

    return charge.amount - charge.amount_refunded;
  };

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="py-2 text-sm">
                <div className="text-sm font-bold">Total</div>
              </TableCell>
              <TableCell className="py-2 text-sm text-right">
                <div className="text-base font-bold">
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(totalAmount)}
                </div>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
            {sortedCharges.map((charge: Charge) => (
              <TableRow key={charge.id}>
                <TableCell className="py-2 text-sm">
                  <div>
                    {charge.kind === "refund"
                      ? "Refund"
                      : getPurposeLabel(charge.purpose)}
                  </div>
                  <div className="text-xs text-stone-700">
                    {charge.status !== "pending" &&
                      format(parseISO(charge.created_at), "MM/dd hh:mm a")}
                  </div>
                </TableCell>
                <TableCell className="py-2 text-sm text-right">
                  {charge.kind === "refund" && "("}
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(Math.abs(charge.amount))}
                  {charge.kind === "refund" && ")"}
                </TableCell>
                <TableCell className="py-2 text-sm">
                  <div className={getStatusLabel(charge.status).className}>
                    {getStatusLabel(charge.status).label}
                  </div>
                </TableCell>
                <TableCell>
                  {charge.mode_of_payment && (
                    <div>
                      {
                        PAYMENT_METHODS[
                          charge.mode_of_payment as keyof typeof PAYMENT_METHODS
                        ]
                      }
                    </div>
                  )}
                  {charge.mode_of_payment === "card" && (
                    <div className="text-xs text-stone-700">
                      {showCardDetails(charge.payment_method)}
                    </div>
                  )}
                  {charge.mode_of_payment === "check" && (
                    <div className="text-xs text-stone-700">
                      #{charge.payment_method?.check_number}
                    </div>
                  )}
                </TableCell>
                <TableCell className="py-2 text-sm text-right">
                  <div className="flex items-center justify-end gap-2">
                    {charge.refundable && refundAction(charge)}
                    {charge.status === "pending" && pendingActions(charge)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mark as Paid Confirmation Dialog */}
      <AlertDialog
        open={isMarkPaidDialogOpen}
        onOpenChange={setIsMarkPaidDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Please confirm that this payment has been received from the
              customer outside of the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkAsPaid}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Void Confirmation Dialog */}
      <AlertDialog open={isVoidDialogOpen} onOpenChange={setIsVoidDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Please confirm that you want to void (cancel) this pending
              payment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidCharge}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel Payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecordOfflinePaymentSheet
        open={isOfflinePaymentSheetOpen}
        onOpenChange={setIsOfflinePaymentSheetOpen}
        moveId={move.id.toString()}
        onSuccess={refetchMove}
      />
    </div>
  );
};

export default Payments;
